import { Core, Decorative } from "@faire/design-tokens";
import { useOptInNudgeResponse } from "@faire/retailer-visitor-shared/components/App/PopupOptInNudge/useOptInNudgeResponse";
import {
  OptInNudgeInput,
  optInOrigin,
} from "@faire/retailer-visitor-shared/components/Banners/OptInNudgeBanners/OptInNudgeInput";
import { useAppNudgeStore } from "@faire/retailer-visitor-shared/features/Nudges/useAppNudgeStore";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useStrictLocalization } from "@faire/web/common/localization";
import { RouteMatcher } from "@faire/web/common/routes/RouteMatcher";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Button } from "@faire/web/slate/Button";
import { Opacity } from "@faire/web/slate/Color";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { useState } from "@faire/web/ui/hooks/useState";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { IEmailOptInNudgeResponse } from "@faire/web-api--source/com/faire/email/preferences/IEmailOptInNudgeResponse";
import { trackRetailersOptInNudgeExpandcollapseClick } from "@faire/web-api--source/events/retailers/click/optInNudgeExpandCollapse";
import { trackRetailersOptInNudgeDismissDismiss } from "@faire/web-api--source/events/retailers/dismiss/optInNudgeDismiss";
import { ISMSOptInNudgeData } from "@faire/web-api--source/indigofair/data/ISMSOptInNudgeData";
import { MARKETING_EMAIL_OPT_IN_COLLAPSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_EMAIL_OPT_IN_COLLAPSED";
import { MARKETING_EMAIL_OPT_IN_DISMISSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_EMAIL_OPT_IN_DISMISSED";
import { MARKETING_SMS_OPT_IN_COLLAPSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_SMS_OPT_IN_COLLAPSED";
import { MARKETING_SMS_OPT_IN_DISMISSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MARKETING_SMS_OPT_IN_DISMISSED";
import { route as apparelCollectionProductBasedCollectionTokenRoute } from "@faire/web-api--source/routes/www/apparel-collection/product_based_collection_token";
import { route as brandbrandTokenRoute } from "@faire/web-api--source/routes/www/brand/brandToken";
import { route as cartRoute } from "@faire/web-api--source/routes/www/cart";
import { route as checkoutRoute } from "@faire/web-api--source/routes/www/checkout";
import { route as confirmIdentityRoute } from "@faire/web-api--source/routes/www/confirm-identity";
import { route as directbrandTokenOrAliasProductBasedCollectionproductBasedCollectionTokenRoute } from "@faire/web-api--source/routes/www/direct/brandTokenOrAlias/product-based-collection/product_based_collection_token";
import { route as pointOfSaleRoute } from "@faire/web-api--source/routes/www/point-of-sale";
import { route as productBasedCollectionProductBasedCollectionTokenRoute } from "@faire/web-api--source/routes/www/product-based-collection/product_based_collection_token";
import { route as retailerSettingsRoute } from "@faire/web-api--source/routes/www/retailer/settings";
import isUndefined from "lodash/isUndefined";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

const nudgeInteraction = {
  expand: "EXPAND",
  collapse: "COLLAPSE",
};

export enum PopupType {
  EMAIL = "EMAIL",
  SMS = "SMS",
}

const CollapsedNudge: React.FC<{
  nudgeData: Readonly<IEmailOptInNudgeResponse.INudgeData | ISMSOptInNudgeData>;
  handleOnExpand: () => void;
  handleOnDismiss: () => void;
}> = ({ nudgeData, handleOnExpand, handleOnDismiss }) => {
  if (!nudgeData.title) {
    return null;
  }

  return (
    <CollapsedWrapper onClick={handleOnExpand}>
      <Typography variant="paragraphSansMedium">{nudgeData.title}</Typography>
      <Spacer width="2x" />
      <Button variant="tertiary" onClick={handleOnDismiss}>
        <Close fontSize={getSpacing("2x")} />
      </Button>
    </CollapsedWrapper>
  );
};

const ExpandedNudge: React.FC<{
  isSuccessful: boolean;
  nudgeData: Readonly<IEmailOptInNudgeResponse.INudgeData | ISMSOptInNudgeData>;
  handleOnCollapse: () => void;
  handleOnDismiss: () => void;
  handleOnSuccess: () => void;
  type: PopupType;
  isMobile: boolean;
}> = ({
  isSuccessful,
  nudgeData,
  handleOnCollapse,
  handleOnDismiss,
  handleOnSuccess,
  type,
  isMobile,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();

  if (!nudgeData.title || !nudgeData.message || !nudgeData.image_url) {
    return null;
  }

  let imageUrl = nudgeData.image_url;
  if (isMobile && type === PopupType.SMS) {
    imageUrl = (nudgeData as ISMSOptInNudgeData).mobile_image_url ?? imageUrl;
  }

  const optimizedBackgroundUrl = optimizeImageUrl(
    {
      url: imageUrl,
    },
    isSSR,
    { width: 600 }
  );

  const title = !isSuccessful
    ? nudgeData.title
    : strictLocalize({
        defaultMessage: "You’re in!",
        description: {
          text: "Email opt-in nudge popup: header text on successful opt-in",
          img: "https://cdn.faire.com/fastly/33581d41d5d23a685437992d22550a8ea28aa0fd370fa9dc6ece287354f672ec.png",
        },
      });

  let successMessage = strictLocalize({
    defaultMessage: "Look for your first email soon.",
    description: {
      text: "Email opt-in nudge popup: body text on successful opt-in",
      img: "https://cdn.faire.com/fastly/33581d41d5d23a685437992d22550a8ea28aa0fd370fa9dc6ece287354f672ec.png",
    },
  });

  if (type === PopupType.SMS) {
    successMessage = strictLocalize({
      defaultMessage: "Look for your first SMS from us soon.",
      description: {
        text: "SMS opt-in nudge popup: body text on successful opt-in",
        img: "https://cdn.faire.com/fastly/54a7b497adeb1e590ba9a7b7430672faeab1b965fcd1e8d4dca3a3102ce0af1d.png",
      },
    });
  }

  const message = !isSuccessful ? nudgeData.message : successMessage;

  return (
    <ExpandedWrapper>
      <CloseIconWrapper>
        <Button
          data-test-id="nudgeCloseButton"
          variant="tertiary"
          onClick={!isSuccessful ? handleOnCollapse : handleOnDismiss}
        >
          <CloseIcon fontSize={getSpacing("3x")} />
        </Button>
      </CloseIconWrapper>
      <BannerImage src={optimizedBackgroundUrl} $isSSR={isSSR} />
      <Content>
        <Typography variant="paragraphSansMedium">{title}</Typography>
        <Spacer width="2x" />
        <Typography variant="paragraphSansRegular">{message}</Typography>
        {!isSuccessful ? (
          <>
            <Spacer width="2x" />
            <OptInNudgeInput
              origin={optInOrigin.POPUP_PROMPT}
              onSuccess={handleOnSuccess}
              type={type}
              savedPhoneNumber={(nudgeData as ISMSOptInNudgeData).phone_number}
            />
          </>
        ) : (
          <>
            <Spacer width="5x" />
            <CloseButton onClick={handleOnDismiss}>
              <StrictLocalMsg
                defaultMessage="Close"
                description={{ text: "Close a user interface component." }}
              />
            </CloseButton>
          </>
        )}
      </Content>
    </ExpandedWrapper>
  );
};

export const PopupOptInNudge: React.FC = () => {
  const { pathname } = useLocation();
  const { isMobile } = useViewport();
  const retailerToken = getRetailer()?.token;
  const { nudgeResponse, type } = useOptInNudgeResponse(retailerToken ?? "");
  const [{ hasAcknowledgedPopupOptInNudge }, setAppNudgeStore] =
    useAppNudgeStore(["hasAcknowledgedPopupOptInNudge"]);
  const { incrementCount: incrementCountPopupOptInNudgeDismissed } =
    useMilestone(
      type === PopupType.EMAIL
        ? MARKETING_EMAIL_OPT_IN_DISMISSED
        : MARKETING_SMS_OPT_IN_DISMISSED
    );
  const { incrementCount: incrementCountPopupOptInNudgeCollapsed } =
    useMilestone(
      type === PopupType.EMAIL
        ? MARKETING_EMAIL_OPT_IN_COLLAPSED
        : MARKETING_SMS_OPT_IN_COLLAPSED
    );

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);

  const [nudgeData, setNudgeData] = useState<
    IEmailOptInNudgeResponse.INudgeData | ISMSOptInNudgeData | undefined
  >(undefined);

  useEffect(() => {
    setNudgeData(nudgeResponse?.opt_in_nudge_data);
    const collapsed = nudgeResponse?.collapsed;
    if (!isUndefined(collapsed)) {
      setIsCollapsed(collapsed);
    }
  }, [nudgeResponse?.collapsed, nudgeResponse?.opt_in_nudge_data]);

  // requires exact match
  const routeMatcher = new RouteMatcher([
    brandbrandTokenRoute,
    confirmIdentityRoute,
    productBasedCollectionProductBasedCollectionTokenRoute,
    apparelCollectionProductBasedCollectionTokenRoute,
    directbrandTokenOrAliasProductBasedCollectionproductBasedCollectionTokenRoute,
  ]);

  const isExcludedRoutes = () => {
    return (
      !!routeMatcher.match(pathname) ||
      pathname.startsWith(cartRoute) ||
      pathname.startsWith(checkoutRoute) ||
      pathname.startsWith(retailerSettingsRoute) ||
      pathname.startsWith(pointOfSaleRoute)
    );
  };

  const shouldShowPopupNudge = !(
    isUndefined(nudgeData) ||
    isExcludedRoutes() ||
    hasAcknowledgedPopupOptInNudge
  );

  if (!shouldShowPopupNudge) {
    return null;
  }

  const handleOnClickOutside = () => {
    if (isCollapsed) {
      return;
    }

    if (isSuccessful) {
      handleOnDismiss();
    }
    handleOnCollapse();
  };

  const handleOnCollapse = () => {
    incrementCountPopupOptInNudgeCollapsed();
    trackRetailersOptInNudgeExpandcollapseClick(nudgeInteraction.collapse);
    setIsCollapsed(true);
  };

  const handleOnExpand = () => {
    trackRetailersOptInNudgeExpandcollapseClick(nudgeInteraction.expand);
    setIsCollapsed(false);
  };

  const handleOnDismiss = () => {
    setAppNudgeStore({
      hasAcknowledgedPopupOptInNudge: true,
    });

    // dismissals when opt-in via nudge is successful shouldn't count
    if (!isSuccessful) {
      incrementCountPopupOptInNudgeDismissed();
      trackRetailersOptInNudgeDismissDismiss();
    }
  };

  const handleOnSuccess = () => {
    setIsSuccessful(true);
  };

  return (
    <NudgeWrapper tabIndex={-1} onClickOutside={handleOnClickOutside}>
      {isCollapsed ? (
        <CollapsedNudge
          nudgeData={nudgeData}
          handleOnExpand={handleOnExpand}
          handleOnDismiss={handleOnDismiss}
        />
      ) : (
        <>
          {isMobile ? (
            <OpaqueBackground onClick={handleOnClickOutside} />
          ) : null}
          <ExpandedNudge
            isSuccessful={isSuccessful}
            nudgeData={nudgeData}
            handleOnCollapse={handleOnCollapse}
            handleOnDismiss={handleOnDismiss}
            handleOnSuccess={handleOnSuccess}
            type={type}
            isMobile={isMobile}
          />
        </>
      )}
    </NudgeWrapper>
  );
};

const NudgeWrapper = styled(ClickOutside)`
  position: fixed;
  right: ${getSpacing("3x")};
  bottom: 0px;
  z-index: 20;

  > div {
    position: relative;
    top: unset;
    left: unset;
    right: unset;
    bottom: "unset";
  }

  button {
    display: flex;
  }

  &&& {
    @media print {
      display: none;
    }
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    min-width: 350px;
    max-width: 432px;
  }

  ${responsiveMediaQueries.mobile} {
    right: 0px;
    left: 0px;
  }
`;

const CollapsedWrapper = styled(Row).attrs({ justify: "space-between" })`
  cursor: pointer;
  padding: ${getSpacing("2x")};
  background-color: ${Decorative.tint.neutral};
  box-shadow: 0px 2px 10px ${Core.surface.default}${Opacity.O_15};

  ${responsiveMediaQueries.tabletAndAbove} {
    width: fit-content;
  }

  ${responsiveMediaQueries.mobile} {
    border-top: 1px solid ${Core.border.muted};
    box-shadow: 0px;
  }
`;

const OpaqueBackground = styled.div`
  height: 100vh;
  background-color: ${Core.surface.enabled};
  opacity: 0.5;
`;

const ExpandedWrapper = styled(Column).attrs({ justify: "space-between" })`
  background: ${Decorative.tint.neutral};
  box-shadow: 0px 2px 10px ${Core.surface.default}${Opacity.O_15};
`;

const CloseIcon = styled(Close)`
  height: ${getSpacing("5x")};
  width: ${getSpacing("5x")};
  padding: 10px;
  background-color: ${Core.surface.subdued};
  border-radius: 100px;
`;

const CloseIconWrapper = styled(Row).attrs({ justify: "end" })`
  z-index: 1;
  position: absolute;
  right: 0;
  margin: ${getSpacing("2x")};
`;

const BannerImage = styled.img<{ $isSSR: boolean }>`
  flex-shrink: 0;
  width: 100%;
  height: 196px;
  object-fit: cover;
  object-position: bottom;
`;

const Content = styled(Column)`
  width: 100%;
  padding: ${getSpacing("3x", "4x")};
`;

const CloseButton = styled(Button)`
  width: fit-content;
  min-width: 86px;
`;
